import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { writeAuditLog } from './auditLog'
import { TeamStatus, TeamType } from '@/types'
import type { Team, UserOp } from '@/types'

export const useTeamsStore = defineStore('teams', () => {
  const teams = ref<Team[]>([])
  const userOps = ref<UserOp[]>([])
  // Drives the pulsing red header alert when any team is in Emergency
  const emergencyTeam = ref<Team | null>(null)

  let teamsUnsubscribe: Unsubscribe | null = null
  let userOpsUnsubscribe: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribeToTeams(incidentId: string, opId: string | null) {
    if (incidentId === _incidentId && opId === _opId && teamsUnsubscribe) return
    if (teamsUnsubscribe) teamsUnsubscribe()
    if (userOpsUnsubscribe) userOpsUnsubscribe()
    _incidentId = incidentId
    _opId = opId ?? ''

    if (!incidentId || !opId) {
      teams.value = []
      userOps.value = []
      emergencyTeam.value = null
      return
    }

    teamsUnsubscribe = onSnapshot(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'teams'),
      (snap) => {
        teams.value = snap.docs.map((d) => ({ team_id: d.id, ...d.data() }) as Team)
        emergencyTeam.value =
          teams.value.find((t) => t.team_status === TeamStatus.Emergency) ?? null
      },
    )

    userOpsUnsubscribe = onSnapshot(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'user_ops'),
      (snap) => {
        userOps.value = snap.docs.map((d) => ({ user_op_id: d.id, ...d.data() }) as UserOp)
      },
    )
  }

  async function createTeam(
    incidentId: string,
    opId: string,
    data?: { team_identifier?: string; team_type?: TeamType; team_status?: TeamStatus },
  ) {
    let identifier = data?.team_identifier

    if (!identifier) {
      // Fetch op_number and existing team count to build the default identifier
      const [teamsSnap, opSnap] = await Promise.all([
        getDocs(collection(db, 'incidents', incidentId, 'operational_periods', opId, 'teams')),
        getDocs(collection(db, 'incidents', incidentId, 'operational_periods')),
      ])
      const opDoc = opSnap.docs.find((d) => d.id === opId)
      const opNumber: number = opDoc?.data().op_number ?? 1
      const nonOverheadCount = teamsSnap.docs.filter(
        (d) => d.data().team_type !== TeamType.Overhead,
      ).length
      const seq = String(nonOverheadCount + 1).padStart(2, '0')
      identifier = `${opNumber}${seq}`
    }

    const docRef = await addDoc(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'teams'),
      {
        team_identifier: identifier,
        team_type: data?.team_type ?? TeamType.Ground,
        team_status: data?.team_status ?? TeamStatus.Building,
        team_leader_personnel_id: null,
        op_id: opId,
        created_at: serverTimestamp(),
      },
    )

    await writeAuditLog(incidentId, 'team.created', 'team', docRef.id, null, {
      team_identifier: identifier,
    })

    return docRef.id
  }

  async function updateTeamIdentifier(
    incidentId: string,
    opId: string,
    teamId: string,
    identifier: string,
  ) {
    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
      { team_identifier: identifier },
    )
    await writeAuditLog(incidentId, 'team.updated', 'team', teamId, null, {
      team_identifier: identifier,
    })
  }

  async function updateTeamType(
    incidentId: string,
    opId: string,
    teamId: string,
    teamType: TeamType,
  ) {
    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
      { team_type: teamType },
    )
    await writeAuditLog(incidentId, 'team.updated', 'team', teamId, null, {
      team_type: teamType,
    })
  }

  async function updateTeamStatus(
    incidentId: string,
    opId: string,
    teamId: string,
    newStatus: TeamStatus,
  ) {
    const team = teams.value.find((t) => t.team_id === teamId)
    const oldStatus = team?.team_status ?? null

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
      { team_status: newStatus },
    )

    await writeAuditLog(
      incidentId,
      'team.status_changed',
      'team',
      teamId,
      { team_status: oldStatus },
      { team_status: newStatus },
    )

    // Emergency rule: surface alert immediately — do not wait for onSnapshot
    if (newStatus === TeamStatus.Emergency && team) {
      emergencyTeam.value = { ...team, team_status: TeamStatus.Emergency }
    }
  }

  async function unassignMemberFromTeam(incidentId: string, opId: string, userId: string | null, personnelId?: string) {
    const userOpsCol = collection(
      db,
      'incidents',
      incidentId,
      'operational_periods',
      opId,
      'user_ops',
    )
    const activeSnap = personnelId
      ? await getDocs(query(userOpsCol, where('personnel_id', '==', personnelId), where('active', '==', true)))
      : userId
        ? await getDocs(query(userOpsCol, where('user_id', '==', userId), where('active', '==', true)))
        : { docs: [] }
    for (const d of activeSnap.docs) {
      await updateDoc(d.ref, { active: false })
    }
    await writeAuditLog(incidentId, 'team.unassigned', 'team', userId ?? '', null, { user_id: userId })
  }

  async function assignMemberToTeam(
    incidentId: string,
    opId: string,
    userId: string | null,
    teamId: string,
    personnelId: string,
  ) {
    const userOpsCol = collection(
      db,
      'incidents',
      incidentId,
      'operational_periods',
      opId,
      'user_ops',
    )

    // Deactivate existing active records for this person:
    // 1. Any record with matching personnel_id (across all teams — rule: one active team at a time)
    // 2. Any legacy record (no personnel_id) with this user_id on this specific team (cleanup)
    const toDeactivate: string[] = []
    const byPersonnel = await getDocs(
      query(userOpsCol, where('personnel_id', '==', personnelId), where('active', '==', true)),
    )
    byPersonnel.forEach((d) => toDeactivate.push(d.id))
    // Also deactivate legacy records (no personnel_id) for this user on this team
    if (userId) {
      const legacySnap = await getDocs(
        query(userOpsCol, where('user_id', '==', userId), where('team_id', '==', teamId), where('active', '==', true)),
      )
      legacySnap.forEach((d) => {
        const data = d.data()
        if (!data.personnel_id && !toDeactivate.includes(d.id)) toDeactivate.push(d.id)
      })
    }
    for (const id of toDeactivate) {
      await updateDoc(doc(userOpsCol, id), { active: false })
    }

    // Create new active record (historical records are preserved, never deleted)
    await addDoc(userOpsCol, {
      user_id: userId,
      personnel_id: personnelId,
      op_id: opId,
      team_id: teamId,
      active: true,
      role: 'Team Member',
      created_at: serverTimestamp(),
    })

    await writeAuditLog(incidentId, 'team.assigned', 'team', teamId, null, {
      user_id: userId,
      team_id: teamId,
    })
  }

  /** Update the overhead role for a person on a specific team. Uses personnel_id when available to avoid collisions when multiple personnel share the same user_id. */
  async function setMemberRole(
    incidentId: string,
    opId: string,
    teamId: string,
    userId: string | null,
    role: string,
    personnelId?: string,
  ) {
    const userOpsCol = collection(
      db,
      'incidents',
      incidentId,
      'operational_periods',
      opId,
      'user_ops',
    )
    // Prefer keying by personnel_id (unique per person) over user_id (shared when same account checks in multiple times)
    const snap = personnelId
      ? await getDocs(query(userOpsCol, where('personnel_id', '==', personnelId), where('team_id', '==', teamId), where('active', '==', true)))
      : userId
        ? await getDocs(query(userOpsCol, where('user_id', '==', userId), where('team_id', '==', teamId), where('active', '==', true)))
        : { docs: [] }
    for (const d of snap.docs) {
      await updateDoc(d.ref, { role })
    }
    await writeAuditLog(incidentId, 'team.role_changed', 'team', teamId, null, {
      user_id: userId,
      role,
    })
  }

  async function setTeamLeader(
    incidentId: string,
    opId: string,
    teamId: string,
    personnelId: string | null,
  ) {
    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
      { team_leader_personnel_id: personnelId },
    )
    await writeAuditLog(incidentId, 'team.leader_changed', 'team', teamId, null, {
      team_leader_personnel_id: personnelId,
    })
  }

  // Returns the team the user is currently active on for the subscribed OP
  function getUserCurrentTeam(userId: string): Team | null {
    const activeUserOp = userOps.value.find((uo) => uo.user_id === userId && uo.active)
    if (!activeUserOp?.team_id) return null
    return teams.value.find((t) => t.team_id === activeUserOp.team_id) ?? null
  }

  // Active members for a given team
  const getTeamMembers = computed(
    () => (teamId: string) => userOps.value.filter((uo) => uo.team_id === teamId && uo.active),
  )

  function teardown() {
    if (teamsUnsubscribe) teamsUnsubscribe()
    if (userOpsUnsubscribe) userOpsUnsubscribe()
    teamsUnsubscribe = null
    userOpsUnsubscribe = null
    _incidentId = ''
    _opId = ''
    teams.value = []
    userOps.value = []
    emergencyTeam.value = null
  }

  return {
    teams,
    userOps,
    emergencyTeam,
    getTeamMembers,
    subscribeToTeams,
    createTeam,
    updateTeamIdentifier,
    updateTeamType,
    updateTeamStatus,
    setTeamLeader,
    setMemberRole,
    assignMemberToTeam,
    unassignMemberFromTeam,
    getUserCurrentTeam,
    teardown,
  }
})
